/*
 * CostCenterCategoryForm.tsx : Un formulaire pour ajouter ou modifier une catégorie de centre de coûts.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCubes } from '@fortawesome/free-solid-svg-icons';

// Import Communs
import {
	finishFormSave,
	FormSaveActions,
	InputField,
	useFormDirty,
} from '../../../common';

// Import Centre de coûts - Catégories
import { CostCenterCategoryData } from '../types/types';
import { costCenterCategoriesApi } from '../services/costCenterCategoriesApi';

// Composant CostCenterCategoryForm
const CostCenterCategoryForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [formData, setFormData] = React.useState<CostCenterCategoryData>({
		id: 0,
		label: '',
	});

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Une fois le composant monté et si un id est présent, fetch des données et préremplissage du formulaire
	React.useEffect(() => {
		if (id) {
			const token = localStorage.getItem('token');
			if (!token) return;

			costCenterCategoriesApi
				.get(Number(id), token)
				.then((data) => setFormData(data))
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Gestion des changements dans le formulaire
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/settings/cost-center-categories');
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			costCenterCategoriesApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			costCenterCategoriesApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(
										`/app/settings/cost-center-categories/${created.id}/edit`,
										{ replace: true }
									)
							: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<div className="flex items-start mb-3 space-x-4">
					{/* Icône à gauche */}
					<FontAwesomeIcon
						icon={faCubes}
						className="w-20 h-20 text-blue-900/90"
					/>

					{/* Titre + toggle à droite */}
					<div className="flex flex-col mt-3">
						<h1 className="text-3xl font-bold">
							{id
								? "Édition d'une catégorie de centre de coûts"
								: 'Nouvelle catégorie de centre de coûts'}
						</h1>
					</div>
				</div>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 1 COLONNE */}
						<div className="grid grid-cols-1 md:grid-cols-1 gap-16">
							{/* COLONNE GAUCHE */}
							<div className="space-y-4">
								<InputField
									label="Label"
									name="label"
									value={formData.label}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "Amortissements"'
								/>
							</div>
						</div>
					</form>
					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => navigate('/app/settings/cost-center-categories')}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default CostCenterCategoryForm;
